import React, { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

interface ServiceArea {
  id: string;
  label: string;
  sub: string;
  center: [number, number]; // [lng, lat]
  radiusKm: number;
  zoom: number;
}

const AREAS: ServiceArea[] = [
  {
    id: "pa",
    label: "Pennsylvania",
    sub: "Within 2hrs of Levittown, PA",
    center: [-74.8496, 40.1551],
    radiusKm: 160,
    zoom: 6.2,
  },
  {
    id: "nj",
    label: "New Jersey",
    sub: "Within 1hr of JBMDL, NJ",
    center: [-74.5932, 40.0182],
    radiusKm: 80,
    zoom: 7.2,
  },
  {
    id: "oh",
    label: "Ohio",
    sub: "Within 1hr of Lima, OH",
    center: [-84.1052, 40.7448],
    radiusKm: 80,
    zoom: 7.2,
  },
];

function circleGeoJSON(center: [number, number], radiusKm: number, points = 64) {
  const [lng, lat] = center;
  const coords: [number, number][] = [];
  for (let i = 0; i < points; i++) {
    const angle = (i / points) * 2 * Math.PI;
    const dx = (radiusKm / 111.32) * Math.cos(angle) / Math.cos((lat * Math.PI) / 180);
    const dy = (radiusKm / 110.574) * Math.sin(angle);
    coords.push([lng + dx, lat + dy]);
  }
  coords.push(coords[0]!);
  return { type: "Feature" as const, geometry: { type: "Polygon" as const, coordinates: [coords] }, properties: {} };
}

export function ServiceAreaMap() {
  const [selected, setSelected] = useState<ServiceArea | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<maplibregl.Map | null>(null);

  useEffect(() => {
    if (!selected || !mapRef.current) return;

    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    const map = new maplibregl.Map({
      container: mapRef.current,
      // Free vector style from OpenFreeMap — swap for a paid style in production
      style: "https://tiles.openfreemap.org/styles/liberty",
      center: selected.center,
      zoom: selected.zoom,
      attributionControl: false,
    });

    mapInstanceRef.current = map;

    map.on("load", () => {
      const geojson = circleGeoJSON(selected.center, selected.radiusKm);

      map.addSource("service-area", { type: "geojson", data: geojson });

      map.addLayer({
        id: "service-area-fill",
        type: "fill",
        source: "service-area",
        paint: { "fill-color": "#1E5EFF", "fill-opacity": 0.12 },
      });

      map.addLayer({
        id: "service-area-border",
        type: "line",
        source: "service-area",
        paint: { "line-color": "#1E5EFF", "line-width": 2, "line-opacity": 0.8 },
      });

      new maplibregl.Marker({ color: "#1E5EFF" })
        .setLngLat(selected.center)
        .addTo(map);
    });

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, [selected]);

  return (
    <section className="py-28 border-b" style={{ background: "#050505", borderColor: "#1a1a1a" }} id="areas">
      <div className="max-w-6xl mx-auto px-6">

        {/* Header */}
        <div className="mb-12">
          <div
            className="flex items-center gap-3 mb-4"
            style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.3em", textTransform: "uppercase", color: "#1E5EFF" }}
          >
            <span style={{ width: 24, height: 2, background: "#1E5EFF", display: "inline-block", flexShrink: 0 }} />
            Service Areas
          </div>
          <h2
            className="font-black italic uppercase mb-4"
            style={{ fontFamily: "var(--font-display)", fontSize: "clamp(2.5rem,5vw,4.5rem)", lineHeight: 0.9, color: "#F2F2F0" }}
          >
            We Come<br />To You
          </h2>
          <p style={{ color: "#808080", fontSize: "0.95rem" }}>
            Choose a region to see our coverage area.
          </p>
        </div>

        {/* Area selector */}
        <div className="grid grid-cols-3 gap-2 mb-8">
          {AREAS.map((area) => (
            <button
              key={area.id}
              onClick={() => setSelected(area)}
              className="py-5 px-6 border text-left transition-all duration-200"
              style={{
                borderRadius: 0,
                background: selected?.id === area.id ? "#1E5EFF" : "#0F0F0F",
                borderColor: selected?.id === area.id ? "#1E5EFF" : "#252525",
                color: selected?.id === area.id ? "#fff" : "#F2F2F0",
                boxShadow: selected?.id === area.id ? "0 0 24px rgba(30,94,255,0.3)" : "none",
              }}
            >
              <div
                className="font-black italic uppercase mb-1"
                style={{ fontFamily: "var(--font-display)", fontSize: "1.2rem", letterSpacing: "0.04em" }}
              >
                {area.label}
              </div>
              <div
                className="text-xs font-medium"
                style={{ color: selected?.id === area.id ? "rgba(255,255,255,0.65)" : "#808080" }}
              >
                {area.sub}
              </div>
            </button>
          ))}
        </div>

        {/* Map */}
        {selected ? (
          <div
            ref={mapRef}
            style={{ width: "100%", height: 480, border: "1px solid #252525" }}
          />
        ) : (
          <div
            className="flex items-center justify-center"
            style={{ width: "100%", height: 480, border: "1px solid #252525", background: "#0F0F0F" }}
          >
            <div className="text-center">
              <div style={{ color: "#1E5EFF", marginBottom: 12 }}>
                <svg width="40" height="40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} style={{ margin: "0 auto" }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                </svg>
              </div>
              <p className="font-bold uppercase text-xs tracking-widest" style={{ color: "#555" }}>
                Select a region above
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
